import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Label,
  Row,
} from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import { useNavigate } from "react-router-dom"
import * as Yup from "utils/yup"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import {
  getGlobalSettingByType,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import Switch from "components/Shared/Switch"
import ConfirmModal from "components/Common/ConfirmModal"

const initialValues = {
  enabled: false,
  cancelWindowMinutes: 5,
}

const OrderCancelSettings = () => {
  const { t } = useTranslation()
  document.title = t("Order Cancel Settings ")

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { loading, isLoading, orderCancelSettings } = useSelector(
    state => state.GlobalSettings
  )
  const { userRole, accountType } = useSelector(state => state.Login)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })
  const [formValues, setFormValues] = useState(initialValues)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [pendingValues, setPendingValues] = useState(null)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      50,
      "order_cancel_settings_1",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      50,
      "order_cancel_settings_3",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole, accountType, navigate])

  useEffect(() => {
    fetchGlobalSettings()
  }, [])

  useEffect(() => {
    if (orderCancelSettings?.orderCancelSettings) {
      const { enabled, cancelWindowMinutes } =
        orderCancelSettings.orderCancelSettings
      setFormValues({
        enabled: enabled ?? false,
        cancelWindowMinutes: cancelWindowMinutes ?? 5,
      })
    }
  }, [orderCancelSettings])

  const fetchGlobalSettings = () => {
    dispatch(getGlobalSettingByType({ type: "orderCancelSettings" }))
  }

  const validationSchema = Yup.object({
    enabled: Yup.boolean(),
    cancelWindowMinutes: Yup.number().when("enabled", {
      is: true,
      then: schema =>
        schema
          .required(t("Cancellation time is required"))
          .integer(t("Must be a whole number"))
          .min(1, t("Minimum is 1 minute"))
          .max(1440, t("Maximum is 1440 minutes (24 hours)")),
      otherwise: schema => schema.optional(),
    }),
  })

  const handleOnSubmit = values => {
    setPendingValues(values)
    setShowSaveModal(true)
  }

  const confirmSave = () => {
    if (!pendingValues) return
    const { enabled, cancelWindowMinutes } = pendingValues
    const payload = {
      id: orderCancelSettings._id,
      type: "orderCancelSettings",
      orderCancelSettings: {
        enabled,
        cancelWindowMinutes: enabled ? Number(cancelWindowMinutes) : 5,
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowSaveModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
  }

  if (loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Formik
          initialValues={formValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values, setFieldValue }) => (
            <Row>
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <CardTitle>{t("Order Cancel Settings")}</CardTitle>
                    <p className="text-muted">
                      {t(
                        "Allow customers to cancel orders within a set time after placing the order. When disabled, cancellation is not limited by this window (existing order status rules still apply)."
                      )}
                    </p>
                    <Form>
                      <Row className="mb-4">
                        <Col lg={12}>
                          <div className="d-flex justify-content-between align-items-center">
                            <Label className="col-form-label mb-0">
                              {t("Enable cancellation time window")}
                            </Label>
                            <Switch
                              onClickHandler={() =>
                                setFieldValue("enabled", !values.enabled)
                              }
                              isChecked={values.enabled}
                              disabled={!usersPermissions.edit}
                            />
                          </div>
                        </Col>
                      </Row>

                      {values.enabled && (
                        <Row className="mb-4">
                          <Col md={12}>
                            <div className="form_field">
                              <Label htmlFor="cancelWindowMinutes">
                                <span className="text-danger">*</span>
                                {t("Cancellation window (minutes)")}
                              </Label>
                              <Field
                                name="cancelWindowMinutes"
                                type="number"
                                min={1}
                                max={1440}
                                className="form-control"
                                disabled={!usersPermissions.edit}
                              />
                              <ErrorMessage
                                name="cancelWindowMinutes"
                                component={TextError}
                              />
                              <small className="text-muted">
                                {t(
                                  "e.g. 5 means the customer can cancel until 5 minutes after the order is created."
                                )}
                              </small>
                            </div>
                          </Col>
                        </Row>
                      )}

                      {usersPermissions.edit && (
                        <div className="d-flex justify-content-end">
                          <Button
                            color="primary"
                            type="submit"
                            disabled={isLoading}
                          >
                            {isLoading ? t("Saving...") : t("Save")}
                          </Button>
                        </div>
                      )}
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Formik>

        <ConfirmModal
          show={showSaveModal}
          onDeleteClick={confirmSave}
          onCloseClick={() => setShowSaveModal(false)}
          isLoading={isLoading}
          buttonText={t("Yes")}
          bodyTitle={t(
            "Are you sure you want to save the order cancellation settings?"
          )}
        />
      </div>
    </div>
  )
}

export default OrderCancelSettings
